/* Investor Onboarding page: validation, anchor scrolling, entrance/reveal/parallax motion, optional Lenis.
   Loaded only by sections/investor-onboarding.liquid. No globals are required by the theme. */
(function () {
  'use strict';

  var roots = document.querySelectorAll('[data-io-root]');
  if (!roots.length) return;

  var mqReduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var reduceMotion = !!(mqReduce && mqReduce.matches);
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var lenis = null;

  /* ---------------- Lenis (scoped to this page) ---------------- */

  var PREVENT_SELECTOR = [
    '[data-lenis-prevent]',
    'dialog',
    '[role="dialog"]',
    '[aria-modal="true"]',
    '[class*="drawer"]',
    '[class*="modal"]',
    '[class*="popup"]',
    '[class*="klaviyo"]',
    '[class*="stickycart"]',
    'iframe'
  ].join(',');

  function initLenis(root) {
    if (lenis || reduceMotion || designMode) return;
    if (root.getAttribute('data-smooth-scroll') !== 'true') return;
    if (typeof window.Lenis !== 'function') return;
    // Respect any smooth-scroll instance that already exists on the page.
    if (window.lenis || document.documentElement.classList.contains('lenis')) return;

    try {
      lenis = new window.Lenis({
        lerp: 0.12,
        smoothWheel: true,
        syncTouch: false, // touch devices keep native scrolling
        allowNestedScroll: true,
        prevent: function (node) {
          return !!(node && node.closest && node.closest(PREVENT_SELECTOR));
        }
      });
    } catch (e) {
      lenis = null;
      return;
    }

    function raf(time) {
      if (!lenis) return;
      lenis.raf(time);
      window.requestAnimationFrame(raf);
    }
    window.requestAnimationFrame(raf);

    // Keep GSAP ScrollTrigger (loaded by the theme) in sync.
    if (window.ScrollTrigger && typeof window.ScrollTrigger.update === 'function') {
      lenis.on('scroll', window.ScrollTrigger.update);
    }

    // Pause Lenis whenever the theme locks page scroll (menu drawer, cart drawer, popups).
    var pausedForLock = false;
    function isLocked() {
      var body = document.body;
      var html = document.documentElement;
      var cls = (body.className || '') + ' ' + (html.className || '');
      if (/(^|\s)(overflow-hidden|no-scroll|scroll-lock|modal-open|lock-scroll)[\w-]*/.test(cls)) return true;
      var bo = window.getComputedStyle(body).overflowY;
      return bo === 'hidden' || bo === 'clip';
    }
    function syncLock() {
      var locked = isLocked();
      if (locked && !pausedForLock) { pausedForLock = true; lenis.stop(); }
      else if (!locked && pausedForLock) { pausedForLock = false; lenis.start(); }
    }
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(syncLock);
      mo.observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });
      mo.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
    }

    // Turn off if the user switches on reduced motion mid-visit.
    if (mqReduce && typeof mqReduce.addEventListener === 'function') {
      mqReduce.addEventListener('change', function (e) {
        if (e.matches && lenis) { lenis.destroy(); lenis = null; reduceMotion = true; }
      });
    }

    window.ioLenis = lenis; // debugging handle only
  }

  /* ---------------- Anchor scrolling with focus management ---------------- */

  function scrollToEl(target, opts) {
    opts = opts || {};
    var focus = function () {
      if (opts.focus) {
        try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
      }
    };
    if (lenis) {
      lenis.scrollTo(target, { offset: opts.offset || -24, onComplete: focus });
    } else {
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: opts.block || 'start' });
      focus();
    }
  }

  function initAnchors(root) {
    root.addEventListener('click', function (e) {
      var link = e.target.closest ? e.target.closest('a[data-io-anchor]') : null;
      if (!link) return;
      var hash = link.getAttribute('href') || '';
      if (hash.charAt(0) !== '#') return;
      var target = document.getElementById(hash.slice(1));
      if (!target) return;
      e.preventDefault();
      scrollToEl(target, { focus: true });
      if (window.history && history.pushState) history.pushState(null, '', hash);
    });
  }

  /* ---------------- Motion: entrance, reveal, parallax, progress rail ---------------- */

  function initMotion(root) {
    var html = document.documentElement;
    var motionOn = html.classList.contains('io-js-motion') && !reduceMotion && !designMode &&
      root.getAttribute('data-reveal') === 'true';

    if (!motionOn) {
      // Setting switched off (or no motion allowed): make sure nothing stays hidden.
      html.classList.remove('io-js-motion');
      return;
    }

    var anims = Array.prototype.slice.call(root.querySelectorAll('[data-io-anim]'));
    var reveal = function (el) { el.classList.add('is-in'); };

    // Hero: staggered entrance on the next frame (delays come from --io-d in CSS).
    window.requestAnimationFrame(function () {
      html.classList.add('io-motion-ready');
      anims.filter(function (el) { return el.getAttribute('data-io-anim') === 'hero'; }).forEach(reveal);
    });

    var rest = anims.filter(function (el) { return el.getAttribute('data-io-anim') !== 'hero'; });
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { reveal(entry.target); io.unobserve(entry.target); }
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });
      rest.forEach(function (el) { io.observe(el); });
    } else {
      rest.forEach(reveal);
    }

    // Never leave focused content invisible (keyboard users tabbing ahead).
    root.addEventListener('focusin', function (e) {
      var el = e.target.closest ? e.target.closest('[data-io-anim]') : null;
      if (el) reveal(el);
    });

    // Scroll-linked depth: decorative layers drift at different speeds, and the
    // process rail fills as the stages come into view. Transform-only, rAF-throttled.
    var layers = Array.prototype.slice.call(root.querySelectorAll('[data-io-parallax]')).map(function (el) {
      return { el: el, speed: parseFloat(el.getAttribute('data-io-parallax')) || 0, host: el.closest('section') || el.parentNode };
    });
    var progressHost = root.querySelector('[data-io-progress]');
    var stages = progressHost ? progressHost.querySelector('.io-stages') : null;
    var ticking = false;

    function update() {
      ticking = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      layers.forEach(function (l) {
        var r = l.host.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        var offset = (r.top + r.height / 2 - vh / 2) * -l.speed;
        offset = Math.max(-140, Math.min(140, offset));
        l.el.style.setProperty('--io-py', offset.toFixed(1) + 'px');
      });
      if (stages) {
        var s = stages.getBoundingClientRect();
        var p = (vh * 0.9 - s.top) / (s.height * 0.75 + vh * 0.15);
        p = Math.max(0, Math.min(1, p));
        progressHost.style.setProperty('--io-progress', p.toFixed(3));
      }
    }
    function requestUpdate() {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }

    if (lenis) lenis.on('scroll', requestUpdate);
    else window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    update();
  }

  /* ---------------- Form validation ---------------- */

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function addToken(el, attr, token) {
    var list = (el.getAttribute(attr) || '').split(/\s+/).filter(Boolean);
    if (list.indexOf(token) === -1) list.push(token);
    el.setAttribute(attr, list.join(' '));
  }
  function removeToken(el, attr, token) {
    var list = (el.getAttribute(attr) || '').split(/\s+/).filter(function (t) { return t && t !== token; });
    if (list.length) el.setAttribute(attr, list.join(' ')); else el.removeAttribute(attr);
  }

  function initForm(root) {
    var form = root.querySelector('form[data-io-form]');
    var success = root.querySelector('[data-io-success]');
    var serverErrors = root.querySelector('[data-io-server-errors]');

    // Delay so theme/app scripts that grab focus on load don't override this.
    var focusLater = function (el) { window.setTimeout(function () { el.focus({ preventScroll: true }); }, 400); };
    if (success) { focusLater(success); return; }
    if (serverErrors) focusLater(serverErrors);
    if (!form) return;

    form.setAttribute('novalidate', '');
    var inputs = form.querySelectorAll('[data-io-validate]');
    var summary = form.querySelector('[data-io-summary]');
    var summaryList = form.querySelector('[data-io-summary-list]');
    var submitBtn = form.querySelector('[data-io-submit]');
    var submitting = false;

    function messageFor(input) {
      var value = (input.value || '').trim();
      if (input.type === 'checkbox') return input.checked ? '' : input.getAttribute('data-io-message');
      if (!value) return input.required ? input.getAttribute('data-io-message') : '';
      if (input.type === 'email' && !EMAIL_RE.test(value)) return input.getAttribute('data-io-format-message');
      if (input.type === 'tel' && value.replace(/\D/g, '').length < 7) return input.getAttribute('data-io-format-message');
      return '';
    }

    function setError(input, msg) {
      var field = input.closest('[data-io-field]');
      var err = field ? field.querySelector('[data-io-error]') : null;
      if (!err) return;
      if (msg) {
        input.setAttribute('aria-invalid', 'true');
        err.textContent = msg;
        err.hidden = false;
        addToken(input, 'aria-describedby', err.id);
      } else {
        input.removeAttribute('aria-invalid');
        err.textContent = '';
        err.hidden = true;
        removeToken(input, 'aria-describedby', err.id);
      }
    }

    function validate(input) {
      var msg = messageFor(input);
      setError(input, msg);
      return msg;
    }

    Array.prototype.forEach.call(inputs, function (input) {
      var evt = input.type === 'checkbox' ? 'change' : 'blur';
      input.addEventListener(evt, function () {
        if (input.type === 'checkbox' || input.value.trim() || input.getAttribute('aria-invalid') === 'true') validate(input);
      });
      input.addEventListener('input', function () {
        if (input.getAttribute('aria-invalid') === 'true') validate(input);
      });
    });

    form.addEventListener('submit', function (e) {
      if (submitting) { e.preventDefault(); return; }

      Array.prototype.forEach.call(form.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"]'), function (i) {
        i.value = i.value.trim();
      });

      var errors = [];
      Array.prototype.forEach.call(inputs, function (input) {
        var msg = validate(input);
        if (msg) errors.push({ input: input, msg: msg });
      });

      if (errors.length) {
        e.preventDefault();
        summaryList.innerHTML = '';
        errors.forEach(function (item) {
          var li = document.createElement('li');
          var a = document.createElement('a');
          a.href = '#' + item.input.id;
          a.textContent = item.msg;
          a.addEventListener('click', function (ev) {
            ev.preventDefault();
            scrollToEl(item.input, { offset: -120, block: 'center' });
            item.input.focus({ preventScroll: true });
          });
          li.appendChild(a);
          summaryList.appendChild(li);
        });
        summary.hidden = false;
        scrollToEl(summary, { focus: true, offset: -120 });
        return;
      }

      summary.hidden = true;
      submitting = true;
      if (submitBtn) submitBtn.setAttribute('aria-busy', 'true');
    });

    // Re-enable if the browser restores the page from the back/forward cache.
    window.addEventListener('pageshow', function () {
      submitting = false;
      if (submitBtn) submitBtn.removeAttribute('aria-busy');
    });
  }

  /* ---------------- Boot ---------------- */

  /* ---------------- Sticky offset (clears the site header if it is sticky/fixed) ---------------- */

  function initStickyOffset(root) {
    var header = document.querySelector('.shopify-section-header, .header-wrapper, sticky-header');
    function measure() {
      var off = 0;
      if (header) {
        var pos = window.getComputedStyle(header).position;
        if (pos === 'sticky' || pos === 'fixed') off = Math.round(header.getBoundingClientRect().height);
      }
      root.style.setProperty('--io-sticky-offset', off + 'px');
    }
    measure();
    window.addEventListener('resize', measure, { passive: true });
  }

  function boot() {
    Array.prototype.forEach.call(roots, function (root) {
      initStickyOffset(root);
      initLenis(root);
      initAnchors(root);
      initMotion(root);
      initForm(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
